import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/nav.scss';
import Logo from '../assets/images/logowhite.png';

const Nav = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const location = useLocation();

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'nl', label: 'Nederlands' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const handleLanguageSelect = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsLanguageOpen(false);
    
    // Refresh ScrollTrigger after language change
    setTimeout(() => {
      ScrollTrigger.refresh();
    }, 100);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setIsLanguageOpen(false);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const primaryLinks = [
    { to: '/', label: t('nav.home'), isLink: true },
    { to: '/services', label: t('nav.services'), isLink: false },
    { to: '/portfolio', label: t('nav.portfolio'), isLink: true },
    { to: '/about', label: t('nav.about'), isLink: false },
    { to: '/contact', label: t('nav.contact'), isLink: false },
  ];

  const secondaryLinks = [
    { href: 'mailto:info@yveswebdesign.com', label: 'info@yveswebdesign.com' },
    { href: 'https://linkedin.com', label: 'LinkedIn', external: true },
    { href: 'https://instagram.com', label: 'Instagram', external: true },
  ];

  return (
    <nav className="nav">
      {/* Top Bar */}
      <div className="nav__bar">
        <div className="nav__container">
          <Link to="/" className="nav__logo" onClick={closeMenu}>
            <img src={Logo} alt="Yves Web Design" />
          </Link>

          {/* Desktop Menu */}
          <ul className="nav__menu">
            {primaryLinks.map((link) => {
              const isActive = location.pathname === link.to;
              return link.isLink ? (
                <li key={link.to}>
                  <Link to={link.to} className={isActive ? 'active' : ''}>
                    {link.label}
                  </Link>
                </li>
              ) : (
                <li key={link.to}>
                  <a href={link.to} className={isActive ? 'active' : ''}>
                    {link.label}
                  </a>
                </li>
              );
            })}
          </ul>

          {/* Desktop Language Dropdown */}
          <div className="nav__language">
            <button
              className="nav__language-btn"
              onClick={() => setIsLanguageOpen(!isLanguageOpen)}
            >
              <span>{currentLanguage.label}</span>
              <svg
                className={`nav__language-arrow ${isLanguageOpen ? 'open' : ''}`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M2.5 4.5L6 8L9.5 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {isLanguageOpen && (
              <ul className="nav__language-menu">
                {languages.map((language) => (
                  <li
                    key={language.code}
                    onClick={() => handleLanguageSelect(language.code)}
                    className={i18n.language === language.code ? 'active' : ''}
                  >
                    {language.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Mobile Toggle Button */}
          <button
            className={`nav__toggle ${isMenuOpen ? 'open' : ''}`}
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <span className="nav__toggle-line"></span>
            <span className="nav__toggle-line"></span>
          </button>
        </div>
      </div>

      {/* Full-Screen Overlay Menu */}
      <div className={`nav__sheet ${isMenuOpen ? 'open' : ''}`}>
        <div className="nav__sheet-header">
          <Link to="/" className="nav__logo" onClick={closeMenu}>
            <img src={Logo} alt="Yves Web Design" />
          </Link>
          <button
            className="nav__close"
            onClick={closeMenu}
            aria-label="Close menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="nav__sheet-content">
          <ul className="nav__sheet-links">
            {primaryLinks.map((link, index) => {
              const isActive = location.pathname === link.to;
              return link.isLink ? (
                <li key={link.to} style={{ animationDelay: `${0.1 + index * 0.05}s` }}>
                  <Link to={link.to} onClick={closeMenu} className={isActive ? 'active' : ''}>
                    {link.label}
                  </Link>
                </li>
              ) : (
                <li key={link.to} style={{ animationDelay: `${0.1 + index * 0.05}s` }}>
                  <a href={link.to} onClick={closeMenu} className={isActive ? 'active' : ''}>
                    {link.label}
                  </a>
                </li>
              );
            })}
          </ul>

          <div className="nav__sheet-divider"></div>

          <ul className="nav__sheet-secondary">
            {secondaryLinks.map((link, index) => (
              <li key={link.href} style={{ animationDelay: `${0.3 + index * 0.05}s` }}>
                <a
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                  onClick={closeMenu}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Language selector in mobile menu */}
        <div className="nav__sheet-footer">
          <div className="nav__sheet-language">
            {languages.map((language) => (
              <button
                key={language.code}
                className={i18n.language === language.code ? 'active' : ''}
                onClick={() => handleLanguageSelect(language.code)}
              >
                {language.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Nav;
